import CheckedCircleIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/CheckedCircle.js';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import { Box, BoxProps, IconButton, Typography, useTheme } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  atlasFontTextBody,
  atlasInverseGhostHoverSx,
  atlasRadiusLg,
  atlasSpacing,
  atlasToastAlertContainerSx,
  atlasToastAlertIconStripSx,
  atlasToastBodySx,
  type AtlasLensTheme,
} from '../Form/atlasLensStyles';
import { resolveAtlasLensColors } from '../Form/formFieldSpec';
import { ATLAS_LENS_SEMANTIC_VARS } from '../Form/atlasLensTokens';

export type AtlasSuccessToastProps = {
  message: string;
  onDismiss: () => void;
} & Omit<BoxProps, 'children'>;

/** Figma success toast — dark surface, green accent strip, checkmark icon, close icon only (no action row). */
export const AtlasSuccessToast = forwardRef<HTMLDivElement, AtlasSuccessToastProps>(
  function AtlasSuccessToast({ message, onDismiss, ...rest }, ref) {
    const { t } = useTranslation();
    const theme = useTheme() as Theme & LensTheme;
    const atlasTheme = theme as AtlasLensTheme;
    const { tokens } = theme;
    const { toastSurface, typeInverse, typeDefault } = resolveAtlasLensColors(theme);
    const statusSuccessDefault =
      tokens?.semantic?.color?.status?.success?.default?.value ??
      ATLAS_LENS_SEMANTIC_VARS.statusSuccessDefault;
    const iconSize = atlasSpacing(atlasTheme, '3');
    const dismissButtonSize = tokens?.core?.spacing?.['5']?.value ?? '40px';

    return (
      <Box
        ref={ref}
        {...rest}
        role="status"
        aria-live="polite"
        data-qa-anchor="atlas-toast-success"
        sx={{
          ...atlasToastAlertContainerSx(atlasTheme),
          background: toastSurface,
        }}
      >
        <Box
          sx={{
            ...atlasToastAlertIconStripSx(atlasTheme),
            background: statusSuccessDefault,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              color: typeDefault,
              '& svg': {
                width: iconSize,
                height: iconSize,
              },
            }}
          >
            <CheckedCircleIcon />
          </Box>
        </Box>
        <Box sx={atlasToastBodySx(atlasTheme)}>
          <Typography
            sx={{
              ...atlasFontTextBody(atlasTheme),
              color: typeInverse,
              flex: 1,
            }}
          >
            {message}
          </Typography>
          <IconButton
            onClick={onDismiss}
            aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
            sx={{
              flexShrink: 0,
              width: dismissButtonSize,
              height: dismissButtonSize,
              p: atlasSpacing(atlasTheme, '1'),
              borderRadius: atlasRadiusLg(atlasTheme),
              color: typeInverse,
              ...atlasInverseGhostHoverSx(atlasTheme),
            }}
          >
            <CloseIcon style={{ fontSize: iconSize }} />
          </IconButton>
        </Box>
      </Box>
    );
  },
);
